/**
 * Retail V1 master screen registry — runtime SoT (plan §1.1, CODE-G01).
 */
import { MASTER_SCREEN_ENTRIES } from './masterScreenEntries.js';
import type {
  JourneyId,
  MasterScreenEntry,
  MasterScreenId,
  ScreenGroup,
} from './masterScreenRegistry.types.js';

export type {
  BlueprintId,
  BusinessDomain,
  CertificationStatus,
  IdentityState,
  JourneyId,
  MasterScreenEntry,
  MasterScreenId,
  ProductLifecycleState,
  ProductScopeClass,
  ProductScopeLifecycle,
  ScreenGroup,
  SurfaceKind,
} from './masterScreenRegistry.types.js';

/** Canonical count per plan §1.2 audit — includes MS-AUTH-019/020 contact interstitial (94). */
export const MASTER_SCREEN_REGISTRY_COUNT = 94 as const;

/** Forbidden duplicate primary MS IDs (legacy aliases live on `aliasMsIds`). */
export const FORBIDDEN_PRIMARY_MS_IDS = ['MS-COM-040', 'MS-AUTH-018'] as const;

export const MASTER_SCREEN_ENTRIES_LIST: readonly MasterScreenEntry[] = MASTER_SCREEN_ENTRIES;

export const MASTER_SCREEN_REGISTRY: Readonly<Record<MasterScreenId, MasterScreenEntry>> =
  Object.freeze(
    Object.fromEntries(MASTER_SCREEN_ENTRIES.map((entry) => [entry.id, entry])) as Record<
      MasterScreenId,
      MasterScreenEntry
    >,
  );

export function getMasterScreenEntry(msId: MasterScreenId): MasterScreenEntry | undefined {
  return MASTER_SCREEN_REGISTRY[msId];
}

export function assertMasterScreenId(msId: string): msId is MasterScreenId {
  return Object.prototype.hasOwnProperty.call(MASTER_SCREEN_REGISTRY, msId);
}

export function listMasterScreenIds(): MasterScreenId[] {
  return MASTER_SCREEN_ENTRIES.map((entry) => entry.id);
}

export function listMasterScreensByGroup(screenGroup: ScreenGroup): MasterScreenEntry[] {
  return MASTER_SCREEN_ENTRIES.filter((entry) => entry.screenGroup === screenGroup);
}

export function listMasterScreensByJourney(journey: JourneyId | 'SHARED'): MasterScreenEntry[] {
  return MASTER_SCREEN_ENTRIES.filter((entry) => entry.journey === journey);
}

export function listRouteBackedMasterScreens(): MasterScreenEntry[] {
  return MASTER_SCREEN_ENTRIES.filter(
    (entry) => entry.surfaceKind === 'route' || entry.surfaceKind === 'redirect',
  );
}

export function getRegistryMetadata(): {
  entryCount: number;
  repositoryBaselineSha: string;
  docVersion: string;
} {
  return {
    entryCount: MASTER_SCREEN_ENTRIES.length,
    repositoryBaselineSha: 'dea8f3ef1aa667173fbd6bb2c14f4a025e1fe0bd',
    docVersion: '1.0.0',
  };
}
